import * as React from 'react'
import { MoreHorizontal } from 'lucide-react'

import { cn } from '~/lib/utils'
import { ButtonProps, buttonVariants } from '~/components/UI/shadcn/button'
import ArrowRight from '~/components/UI/General/Pagination/icons/arrow-right'
import ArrowLeft from '~/components/UI/General/Pagination/icons/arrow-left'

const Pagination = ({
  className,
  ...props
}: React.ComponentProps<'nav'> & {
  className?: string
}) => (
  <nav
    role='navigation'
    aria-label='pagination'
    className={cn('mx-auto flex w-full', className)}
    {...props}
  />
)
Pagination.displayName = 'Pagination'

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'> & {
    className?: string
  }
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex flex-row items-center pl-0', className)}
    {...props}
  />
))
PaginationContent.displayName = 'PaginationContent'

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'> & {
    className?: string
  }
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
))
PaginationItem.displayName = 'PaginationItem'

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<ButtonProps, 'size'> & {
    className?: string
  } & React.ComponentProps<'a'> & {
    className?: string
  }

const PaginationLink = ({
  className,
  isActive,
  size = 'icon',
  ...props
}: PaginationLinkProps) => (
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  <a
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? 'outline' : 'ghost',
        size
      }),
      className
    )}
    {...props}
  />
)
PaginationLink.displayName = 'PaginationLink'

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink> & {
  className?: string
}) => (
  <PaginationLink
    aria-label='Go to previous page'
    size='default'
    className={cn('gap-xs px-0! py-0', className)}
    {...props}
  >
    <ArrowLeft
      width={'1.25em'}
      height={'1.25em'}
      fill={''}
      title={''}
      strokewidth={2}
    />
    <span className='sr-only'>Previous</span>
  </PaginationLink>
)
PaginationPrevious.displayName = 'PaginationPrevious'

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink> & {
  className?: string
}) => (
  <PaginationLink
    aria-label='Go to next page'
    size='default'
    className={cn('gap-xs px-0! py-0', className)}
    {...props}
  >
    <span className='sr-only'>Next</span>
    <ArrowRight
      width={'1.25em'}
      height={'1.25em'}
      fill={''}
      title={''}
      strokewidth={2}
    />
  </PaginationLink>
)
PaginationNext.displayName = 'PaginationNext'

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<'span'> & {
  className?: string
}) => (
  <span
    aria-hidden
    className={cn('flex items-center justify-center', className)}
    {...props}
  >
    <MoreHorizontal className='h-xs sm:h-s sm:w-s w-xs' />
    <span className='sr-only'>More pages</span>
  </span>
)
PaginationEllipsis.displayName = 'PaginationEllipsis'

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
}
